import { useEffect, useState } from 'react'
import { api, type InboundMessage, type MessageLadder, type VendorMessage } from '../lib/api'
import { ErrorBox, Loading } from './DataTable'
import { filedWhere, messageKind, messageState } from './VendorRiskSummary'
import { ActivityFeed, Status, Toggletip, type Tone } from './kit'
import { ChannelIcon } from './assess/ChannelIcon'
import { ReminderLadder } from './assess/ReminderLadder'
import { channelName, dayMonthTime, tipText } from './assess/journey'
import '../styles/home.css'

// Every message to this vendor's contacts, on every channel, with what became
// of it, and every reply that came back.
//
// # WHY A TAB OF ITS OWN
//
// The delivery receipts were built into the reminder ladder and shown as one
// line, the newest message, on the Risk tab, and only once a message existed.
// "Did they actually get it" is asked before chasing a vendor and again when a
// regulator asks how the vendor was told, and one line answers neither.
//
// # RECEIPTS AT A GLANCE
//
// Each message is one row with the channel's mark and one label for its fate.
// The carrier's own words, and why a message was held back, sit behind the (i)
// on the row, because they matter for the one message somebody is asking about
// and are noise on the other nineteen.

const LADDERS_SHOWN = 3

function outcome(m: VendorMessage): { tone: Tone; text: string } {
  switch (m.status) {
    case 'READ':
      return { tone: 'ok', text: 'Read' }
    case 'DELIVERED':
      return { tone: 'ok', text: 'Delivered' }
    case 'SENT':
      return { tone: 'info', text: 'Sent' }
    case 'SUPPRESSED':
      return { tone: 'neutral', text: 'Held back' }
    case 'MUTED':
      return { tone: 'warn', text: 'Not sent' }
    default:
      return { tone: 'crit', text: 'Not delivered' }
  }
}

// What the reply was, in a few words for its line; the rest is in its tip.
function replyShort(m: InboundMessage): string {
  if (m.kind === 'DOCUMENT' || m.kind === 'IMAGE') return m.fileName ? 'Document, ' + m.fileName : 'A document'
  if (m.kind === 'TEXT') {
    const t = (m.text ?? '').trim()
    return t.length > 48 ? '"' + t.slice(0, 47) + '…"' : '"' + t + '"'
  }
  return 'A message'
}

function filed(m: InboundMessage): { tone: Tone; text: string } {
  switch (m.filedAs) {
    case 'CLARIFICATION_REPLY':
      return { tone: 'ok', text: 'Answer recorded' }
    case 'RESPONSE_EVIDENCE':
      return { tone: 'ok', text: 'Evidence attached' }
    case 'VENDOR_DOCUMENT':
      return { tone: 'warn', text: 'Needs review' }
    case 'ASSESSMENT_MESSAGE':
      return { tone: 'neutral', text: 'Kept' }
    default:
      return { tone: 'warn', text: 'Unfiled' }
  }
}

function capital(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// "the invitation" reads in a sentence; a table cell wants "Invitation".
function kindWord(templateKey: string): string {
  return capital(messageKind(templateKey).replace(/^(the|a|an) /, ''))
}

type Loaded = { messages: VendorMessage[]; inbound: InboundMessage[]; ladders: MessageLadder[] }

export function VendorMessages({ vendorId }: { vendorId: string }) {
  const [data, setData] = useState<Loaded | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [allLadders, setAllLadders] = useState(false)

  useEffect(() => {
    let live = true
    Promise.all([api.vendorMessages(vendorId), api.vendorChannels(vendorId).catch(() => ({ ladders: [] as MessageLadder[] }))])
      .then(([m, c]) => {
        if (live) setData({ messages: m.messages ?? [], inbound: m.inbound ?? [], ladders: c.ladders ?? [] })
      })
      .catch((err) => {
        if (live) setError(err)
      })
    return () => {
      live = false
    }
  }, [vendorId])

  if (error != null) return <ErrorBox error={error} what="The messages" />
  if (!data) return <Loading what="the messages" />

  const ladders = allLadders ? data.ladders : data.ladders.slice(0, LADDERS_SHOWN)

  return (
    <>
      <section className="panel" aria-labelledby="messages-sent">
        <div className="as-section-head">
          <h2 id="messages-sent" className="hm-h2">
            Sent to this vendor <span className="hm-count">{data.messages.length}</span>
          </h2>
          <Toggletip label="About these messages">
            The newest twenty, on email, SMS and WhatsApp. Delivered and read come from the carrier, not from the send.
          </Toggletip>
        </div>
        {data.messages.length === 0 ? (
          <div className="hm-empty">
            <strong>No message to this vendor is on record</strong>
            <span className="as-reply">
              <span className="hm-muted">Messages are recorded as they are sent</span>
              <Toggletip label="Why a sent questionnaire can have no message">
                Messages are recorded from the moment this product sends them. A questionnaire shown as sent with nothing here was sent
                before recording began, or came from imported or demonstration data.
              </Toggletip>
            </span>
          </div>
        ) : (
          <div className="as-table-wrap">
            <table className="as-msg-table">
              <caption className="vh">Messages sent to this vendor</caption>
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">What</th>
                  <th scope="col">Channel</th>
                  <th scope="col" className="as-col-to">
                    To
                  </th>
                  <th scope="col">Outcome</th>
                </tr>
              </thead>
              <tbody>
                {data.messages.map((m, i) => {
                  const o = outcome(m)
                  const detail = capital(messageState(m)) + '.' + (m.detail ? ' ' + capital(m.detail) : '')
                  return (
                    <tr key={i}>
                      <td className="kit-mono">{dayMonthTime(m.createdAt)}</td>
                      <td>{kindWord(m.templateKey)}</td>
                      <td>
                        <span className="as-cell-flex">
                          <ChannelIcon channel={m.channel} size={14} />
                          {channelName(m.channel)}
                        </span>
                      </td>
                      <td className="as-col-to">
                        <span className="kit-mono as-to" title={m.to}>
                          {m.to}
                        </span>
                      </td>
                      <td>
                        <span className="as-cell-flex">
                          <Status tone={o.tone}>{o.text}</Status>
                          <Toggletip label={'What became of ' + messageKind(m.templateKey)}>{tipText(detail)}</Toggletip>
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel" aria-labelledby="messages-back">
        <div className="as-section-head">
          <h2 id="messages-back" className="hm-h2">
            Replies from the vendor <span className="hm-count">{data.inbound.length}</span>
          </h2>
        </div>
        {data.inbound.length === 0 ? (
          <p className="as-line muted">No reply has come back on WhatsApp or SMS</p>
        ) : (
          <ActivityFeed
            items={data.inbound.map((m, i) => {
              const f = filed(m)
              const whole = (m.kind === 'TEXT' && m.text ? '"' + m.text.trim() + '". ' : '') + capital(filedWhere(m))
              return {
                key: String(i),
                tone: f.tone,
                when: dayMonthTime(m.receivedAt) + ' on ' + channelName(m.channel),
                text: (
                  <span className="as-reply">
                    <span>{replyShort(m)}</span>
                    <Status tone={f.tone}>{f.text}</Status>
                    <Toggletip label="Where this reply was filed">{tipText(whole)}</Toggletip>
                  </span>
                ),
              }
            })}
          />
        )}
      </section>

      <section className="panel" aria-labelledby="messages-ladders">
        <div className="as-section-head">
          <h2 id="messages-ladders" className="hm-h2">
            Reminders <span className="hm-count">{data.ladders.length}</span>
          </h2>
          <Toggletip label="How reminders climb">
            A reminder tries each channel in the order set on Settings and stops at the first one confirmed as delivered, or when the
            vendor answers.
          </Toggletip>
        </div>
        {data.ladders.length === 0 ? (
          <p className="as-line muted">No reminder has run for this vendor</p>
        ) : (
          <>
            {ladders.map((l) => (
              <ReminderLadder key={l.id} ladder={l} />
            ))}
            {data.ladders.length > LADDERS_SHOWN && (
              <button type="button" className="linkish" onClick={() => setAllLadders((v) => !v)} aria-expanded={allLadders}>
                {allLadders ? 'Show the newest ' + LADDERS_SHOWN : 'Show ' + (data.ladders.length - LADDERS_SHOWN) + ' earlier'}
              </button>
            )}
          </>
        )}
      </section>
    </>
  )
}
